import { useCallback, useEffect, useState } from 'react';
import {
  Button,
  Dropdown,
  Form,
  InputGroup,
  Modal,
  Spinner,
  Stack,
} from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import { NavLink, useHistory } from 'react-router-dom';

import { faEye, faEyeSlash } from '@fortawesome/free-regular-svg-icons';
import { faLanguage } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { AxiosError } from 'axios';
import microsoftLogo from 'src/assets/img/microsoft.svg';
import oktaLogo from 'src/assets/img/okta.svg';

import useLocalStore from 'src/store/useLocalStore';

import { Api } from 'src/models/Api.model';
import { GalenErrorKeys } from 'src/models/GalenData/GalenError.model';
import {
  LoginType,
  SsoParams,
} from 'src/models/GalenData/LoginUsingPOST.model';
import { AxiosResponseWithRefreshToken } from 'src/models/GalenData/Request.model';
import { GalenDataUser } from 'src/models/GalenData/User.model';
import { getLocaleCode, Language } from 'src/models/Language.model';
import { PracticeSettings } from 'src/models/Settings.model';

import useAutoConnectCamera from 'src/hooks/useAutoConnectCamera';
import useHandleHttpRequestError from 'src/hooks/useHandleHttpRequestError';
import useSetCurrentUserInfo from 'src/hooks/useSetCurrentUserInfo';
import useUiTranslation from 'src/hooks/useUiTranslation';

import {
  generateOAuthState,
  generateRandomString,
} from 'src/utils/generateRandomString';
import { emailPattern } from 'src/utils/validationRules';

type LoginFormFieldTypes = {
  email: string;
  password: string;
};

interface PasswordLoginProps {
  isLoggingIn: boolean;
  setIsLoggingIn: (value: boolean) => void;
  setCurrentPassword: (password: string) => void;
  setXRequestToken: (token: string) => void;
  setShouldShowOTPInput: (value: boolean) => void;
  validateUserRole: (
    res: AxiosResponseWithRefreshToken<GalenDataUser>,
    callback?: () => void,
  ) => boolean;

  setShouldShowResetPassword: (value: boolean) => void;
  setUserId: (userId: string) => void;
  practiceSettings: PracticeSettings;
}

const PasswordLogin = ({
  isLoggingIn,
  setIsLoggingIn,
  setCurrentPassword,
  setXRequestToken,
  setShouldShowOTPInput,
  validateUserRole,
  setShouldShowResetPassword,
  setUserId,
  practiceSettings,
}: PasswordLoginProps) => {
  const {
    rememberUserEmail,
    setRememberUserEmail,
    isRememberEmail,
    setIsRememberEmail,
  } = useLocalStore();

  const { setCurrentUserInfo } = useSetCurrentUserInfo();

  const {
    handleSubmit,
    register,
    formState: { errors },
  } = useForm<LoginFormFieldTypes>({
    defaultValues: {
      email: rememberUserEmail ?? '',
      password: '',
    },
  });
  const { tPages, tCommon, language, changeLanguage } = useUiTranslation();

  const [isShowPassword, setIsShowPassword] = useState(false);
  const history = useHistory();
  const { handleHttpRequestError } = useHandleHttpRequestError();
  const { autoConnectCamera } = useAutoConnectCamera();

  const handleLogin = async ({ email, password }: LoginFormFieldTypes) => {
    try {
      const emailTrim = email.trim();
      const passwordTrim = password.trim();

      setIsLoggingIn(true);
      setCurrentPassword(passwordTrim);

      const res = await Api.remote.login(emailTrim, passwordTrim);

      if (!validateUserRole(res)) {
        return;
      }

      const getXRequestToken = res.headers['x-request-token'];
      setXRequestToken(getXRequestToken);

      if (!res.data) {
        setShouldShowOTPInput(true);
        return;
      }

      await setCurrentUserInfo(res);

      if (res.data?.passwordExpired) {
        setUserId(res.data.userId);
        setShouldShowResetPassword(true);
        return;
      }

      autoConnectCamera(practiceSettings);

      if (isRememberEmail) {
        setRememberUserEmail(emailTrim);
      } else {
        setRememberUserEmail(undefined);
      }

      history.replace('/');
    } catch (err) {
      const errorMessage = handleHttpRequestError(err);
      Api.alertBox({
        title: tCommon('bootbox.title.Error'),
        message: errorMessage,
        locale: getLocaleCode(language as Language),
        type: 'error',
      });
    } finally {
      setIsLoggingIn(false);
    }
  };

  const { setUiLanguage, ssoState, setSsoState } = useLocalStore();

  const [isLoginWithSso, setIsLoginWithSso] = useState(false);

  const handleLogout = useCallback(
    (provider: LoginType) => {
      if (provider === LoginType.Okta) {
        window.location.href = `${import.meta.env.VITE_OKTA_CLIENT_DOMAIN}/login/signout?fromURI=${import.meta.env.VITE_REDIRECT_URI}`;
        return;
      }

      window.history.replaceState({}, '', '/');
      history.replace('/');
    },
    [history],
  );

  const handleSsoRedirectCallback = useCallback(async () => {
    const urlParams = new URLSearchParams(location.search.split('?')[1]);

    const code = urlParams.get(SsoParams.Code);
    const state = urlParams.get(SsoParams.State);

    const decodedState = decodeURIComponent(state || '');
    const provider = decodedState.split('|')[1];

    if (code && state === ssoState) {
      try {
        setIsLoginWithSso(true);
        const res = await Api.remote.oauth2Login({
          code,
          loginType: provider as LoginType,
          redirectUri: import.meta.env.VITE_REDIRECT_URI,
        });

        if (
          !validateUserRole(res, () => {
            handleLogout(provider as LoginType);
          })
        ) {
          return;
        }

        setSsoState(generateRandomString(15));

        await setCurrentUserInfo(res);

        autoConnectCamera(practiceSettings);

        window.history.replaceState({}, '', '/');
        history.replace('/');
      } catch (error) {
        if (
          error instanceof AxiosError &&
          error.response?.status === 403 &&
          error.response?.data?.includes(
            GalenErrorKeys.TENANT_DOES_NOT_ALLOW_SELF_REGISTRATION,
          )
        ) {
          return Api.alertBox({
            title: tCommon('bootbox.title.Error'),
            message: tPages('loginLogout.userNotFoundInOptain'),
            locale: getLocaleCode(language as Language),
            type: 'error',
            callback: () => {
              handleLogout(provider as LoginType);
            },
          });
        }

        const errorMessage = handleHttpRequestError(error);
        Api.alertBox({
          title: tCommon('bootbox.title.Error'),
          message: errorMessage,
          locale: getLocaleCode(language as Language),
          type: 'error',
          callback: () => {
            handleLogout(provider as LoginType);
          },
        });
      }
    }
  }, [
    ssoState,
    validateUserRole,
    setCurrentUserInfo,
    setSsoState,
    autoConnectCamera,
    practiceSettings,
    history,
    handleLogout,
    handleHttpRequestError,
    tPages,
    tCommon,
    language,
  ]);

  useEffect(() => {
    handleSsoRedirectCallback();
  }, [handleSsoRedirectCallback]);

  const handleOktaLogin = useCallback(() => {
    const ssoState = generateOAuthState(LoginType.Okta);

    setSsoState(ssoState);

    const params = new URLSearchParams({
      client_id: import.meta.env.VITE_OKTA_CLIENT_ID,
      redirect_uri: import.meta.env.VITE_REDIRECT_URI,
      state: ssoState,
      response_type: SsoParams.Code,
      scope: import.meta.env.VITE_OKTA_SCOPE,
      prompt: import.meta.env.VITE_OKTA_PROMPT,
    });
    const url = `${import.meta.env.VITE_OKTA_CLIENT_DOMAIN}/oauth2/v1/authorize?${params.toString()}`;

    window.location.href = url;
  }, [setSsoState]);

  const handleMicrosoftLogin = useCallback(() => {
    const ssoState = generateOAuthState(LoginType.Microsoft);

    setSsoState(ssoState);

    const params = new URLSearchParams({
      client_id: import.meta.env.VITE_MICROSOFT_CLIENT_ID,
      response_type: SsoParams.Code,
      redirect_uri: import.meta.env.VITE_REDIRECT_URI,
      state: ssoState,
      scope: import.meta.env.VITE_MICROSOFT_SCOPE,
      prompt: import.meta.env.VITE_MICROSOFT_PROMPT,
    });

    const url = `${import.meta.env.VITE_MICROSOFT_CLIENT_DOMAIN}/common/oauth2/v2.0/authorize?${params.toString()}`;

    window.location.href = url;
  }, [setSsoState]);

  return (
    <>
      {isLoginWithSso ? (
        <div className="p-5 d-flex flex-column justify-content-center align-items-center gap-2">
          <Spinner variant="primary" />
          <p>{tPages('loginLogout.loggingIn')}</p>
        </div>
      ) : (
        <Form onSubmit={handleSubmit(handleLogin)} data-test="login-submit">
          <Modal.Body>
            <Form.Group className="mb-2">
              <Form.Label>
                <strong>{tPages('loginLogout.fields.email')}</strong>
              </Form.Label>
              <Form.Control
                id="email"
                type="text"
                placeholder={tPages('loginLogout.fields.emailPlaceholder')}
                autoComplete="off"
                {...register('email', {
                  required: tPages('loginLogout.fields.emailRequired'),
                  pattern: {
                    value: emailPattern,
                    message: tPages('loginLogout.fields.emailInvalid'),
                  },
                  maxLength: {
                    value: 254,
                    message: tPages('loginLogout.fields.emailMaxLength'),
                  },
                })}
              ></Form.Control>

              {errors.email?.message && (
                <p className="text-danger">{errors.email?.message}</p>
              )}
            </Form.Group>

            <Form.Group className="mb-2">
              <Form.Label>
                <strong>{tPages('loginLogout.fields.password')}</strong>
              </Form.Label>
              <InputGroup>
                <Form.Control
                  id="password"
                  type={isShowPassword ? 'text' : 'password'}
                  placeholder={tPages('loginLogout.fields.passwordPlaceholder')}
                  autoComplete="off"
                  {...register('password', {
                    required: tPages('loginLogout.fields.passwordRequired'),
                    minLength: {
                      value: 8,
                      message: tPages('loginLogout.fields.passwordMinLength'),
                    },
                    maxLength: {
                      value: 128,
                      message: tPages('loginLogout.fields.passwordMaxLength'),
                    },
                  })}
                />

                <InputGroup.Text
                  onClick={() => setIsShowPassword(!isShowPassword)}
                  className="pointer"
                >
                  <FontAwesomeIcon icon={isShowPassword ? faEye : faEyeSlash} />
                </InputGroup.Text>
              </InputGroup>
              {errors.password?.message && (
                <p className="text-danger">{errors.password?.message}</p>
              )}
            </Form.Group>

            <div className="d-flex justify-content-between align-items-center">
              <Form.Check
                type="checkbox"
                label={tPages('loginLogout.rememberEmail')}
                id="rememberEmail"
                checked={isRememberEmail}
                onChange={() => setIsRememberEmail(!isRememberEmail)}
              />

              <NavLink
                to="/forgot-password"
                className="text-primary text-decoration-none"
              >
                {tPages('changePassword.forgotPassword')}
              </NavLink>
            </div>

            <div>
              {isLoggingIn && (
                <p className="alert alert-primary mb-0 mt-2">
                  {tPages('loginLogout.loggingInPleaseWait')}
                </p>
              )}
            </div>
          </Modal.Body>

          <Modal.Footer className="d-block">
            <Stack gap={2}>
              <Button disabled={isLoggingIn} type="submit" variant="primary">
                {isLoggingIn
                  ? tPages('loginLogout.loggingIn')
                  : tPages('loginLogout.login')}
              </Button>
              <p className="text-center fw-bold mb-0">
                {tPages('loginLogout.OR')}
              </p>
              <Button variant="light" onClick={handleMicrosoftLogin}>
                <img
                  src={microsoftLogo}
                  alt="Microsoft"
                  width={20}
                  className="object-fit-contain me-2"
                />
                <span>{tPages('loginLogout.continueWithMicrosoft')}</span>
              </Button>
              <Button variant="light" onClick={handleOktaLogin}>
                <img
                  src={oktaLogo}
                  alt="Okta"
                  width={40}
                  className="object-fit-contain me-2"
                />
                <span>{tPages('loginLogout.continueWithOkta')}</span>
              </Button>

              <Dropdown>
                <Dropdown.Toggle
                  variant="link"
                  className="text-decoration-none text-primary p-0"
                >
                  <FontAwesomeIcon icon={faLanguage} className="me-2" />

                  {tCommon(`report.languageEnum.${language}`)}
                </Dropdown.Toggle>

                <Dropdown.Menu>
                  {Object.values(Language).map((lang) => (
                    <Dropdown.Item
                      key={lang}
                      active={language === lang}
                      onClick={async () => {
                        await changeLanguage(lang);
                        setUiLanguage(lang);
                      }}
                    >
                      {tCommon(`report.languageEnum.${lang}`)}
                    </Dropdown.Item>
                  ))}
                </Dropdown.Menu>
              </Dropdown>
            </Stack>
          </Modal.Footer>
        </Form>
      )}
    </>
  );
};

export default PasswordLogin;
